import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { formatCents } from '@pirouette/shared';
import {
  useMyPaymentPlans,
  useMyInstallments,
  type PaymentPlanRow,
  type PaymentPlanInstallmentRow,
} from '@/data/payments';
import {
  installmentTone,
  planTone,
  planProgress,
  nextDueDate,
} from '@/components/billing/paymentPlanView';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';

export const Route = createFileRoute('/parent/billing/payment-plans')({
  component: ParentPaymentPlans,
});

/**
 * Read-only parent "Payment plans" view. Shows the family's own plan(s) and each
 * plan's installment schedule with statuses and the next due date. RLS scopes
 * every row to the caller's family — no family id is passed from the client.
 */
function ParentPaymentPlans() {
  const plans = useMyPaymentPlans();
  // All of the parent's installments in one read; grouped per plan below so
  // there is no per-plan request waterfall.
  const installments = useMyInstallments();

  // Memoized: react-hooks/exhaustive-deps is an ERROR in this repo, and this
  // builds a fresh Map each render otherwise.
  const installmentsByPlan = useMemo(() => {
    const map = new Map<string, PaymentPlanInstallmentRow[]>();
    for (const inst of installments.data ?? []) {
      const list = map.get(inst.plan_id);
      if (list) list.push(inst);
      else map.set(inst.plan_id, [inst]);
    }
    return map;
  }, [installments.data]);

  const planList = plans.data ?? [];

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Billing
        </p>
        <h1 className="mt-2">Payment plans</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Your scheduled installment plans. Each plan splits a balance across several payments on a
          set cadence. This view is read-only — reach out to the studio with any questions.
        </p>
        <nav className="mt-3 flex flex-wrap gap-3 text-[var(--text-sm)]">
          <Link to="/parent/billing" className="underline">
            Back to billing
          </Link>
        </nav>
      </header>

      {plans.isError && (
        <p className="text-[var(--color-danger)] text-[var(--text-sm)]">
          Couldn't load your payment plans. Please try again.
        </p>
      )}

      {!plans.isError && !plans.isLoading && planList.length === 0 && (
        <Empty
          title="No payment plans"
          description="You don't have any scheduled payment plans right now. When the studio sets one up, it will show here."
        />
      )}

      {planList.map((plan) => (
        <ParentPlanCard
          key={plan.id}
          plan={plan}
          installments={installmentsByPlan.get(plan.id) ?? []}
        />
      ))}
    </div>
  );
}

function ParentPlanCard({
  plan,
  installments,
}: {
  plan: PaymentPlanRow;
  installments: PaymentPlanInstallmentRow[];
}) {
  const progress = useMemo(() => planProgress(installments), [installments]);
  const nextDue = useMemo(() => nextDueDate(installments), [installments]);

  return (
    <section aria-labelledby={`plan-${plan.id}`} className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-[var(--color-border)] pb-2">
        <h2 id={`plan-${plan.id}`} className="text-[var(--text-xl)] tabular-nums">
          {formatCents(plan.total_cents)}{' '}
          <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            · {plan.installment_count} installment{plan.installment_count === 1 ? '' : 's'}
          </span>
        </h2>
        <Badge tone={planTone(plan.status)}>{plan.status}</Badge>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
        <span className="tabular-nums">
          {formatCents(progress.paidCents)} of {formatCents(progress.totalCents)} paid
          {progress.totalCount > 0 && ` (${progress.paidCount}/${progress.totalCount})`}
        </span>
        {nextDue && <span className="tabular-nums">Next due {nextDue}</span>}
      </div>

      {installments.length === 0 ? (
        <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          No installments scheduled yet.
        </p>
      ) : (
        <ul className="grid gap-2">
          {installments.map((inst, idx) => (
            <li key={inst.id} className="surface flex items-center justify-between gap-3 p-3">
              <div className="text-[var(--text-sm)]">
                <span className="text-[var(--color-ink-muted)]">#{idx + 1}</span>{' '}
                <span className="tabular-nums">due {inst.due_date}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-medium tabular-nums">{formatCents(inst.amount_cents)}</span>
                <Badge tone={installmentTone(inst.status)}>{inst.status}</Badge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
